import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Drawer } from '@mui/material';

import { DarkSurface } from '@/components/DarkSurface';
import { SkipToMainContentLink } from '@/components/SkipToMainContentLink';
import { appShellInertDomProps } from '@/hooks/useInertBackground';
import { Logo } from '../../Layout/Logo';
import { useMainLayoutContext } from '../Context/useMainLayoutContext';

interface LeftDrawerV2Props extends PropsWithChildren {
  /** Renders the drawer inside a dark theme (see DarkSurface), with the logo on a white band. */
  dark?: boolean;
}

export const LeftDrawerV2: FC<LeftDrawerV2Props> = ({ children, dark = false }) => {
  const { t } = useTranslation();

  const {
    isDrawerOpen,
    drawerVariant,
    drawerSx,
    isDrawerCollapsed,
    toggleDrawer,
    navigationDrawerPaperId,
    navigationMenuButtonRef,
  } = useMainLayoutContext();

  const handleDrawerClose = () => {
    toggleDrawer(false);
    navigationMenuButtonRef.current?.focus();
  };

  const drawer = (
    <>
      <SkipToMainContentLink />

      <Drawer
        anchor="left"
        variant={drawerVariant}
        open={isDrawerOpen}
        slotProps={{
          paper: {
            id: navigationDrawerPaperId,
            role: 'navigation' as const,
            'aria-label': t('navigationMenu'),
            ...appShellInertDomProps,
            sx: drawerSx,
          },
        }}
        onClose={handleDrawerClose}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.shape.borderRadius,
            ...theme.mixins.toolbar,
            // White band behind the logo so the original-color logo keeps its contrast; the
            // negative margin cancels the drawer's horizontal padding to span the full width.
            ...(dark && {
              bgcolor: '#fff',
              mx: -2,
              px: 2,
              borderRadius: 0,
            }),
          })}
        >
          <Logo height={28} isCollapsed={isDrawerCollapsed} />
        </Box>

        <Divider sx={{ mb: 3, mt: '-1px', ...(dark && { borderColor: 'transparent' }) }} />

        {children}
      </Drawer>
    </>
  );

  return dark ? <DarkSurface>{drawer}</DarkSurface> : drawer;
};
